import { format } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

/**
 * 두 날짜 사이의 일수를 계산하여 "nn일간" 형태로 반환합니다.
 * @param startDate 시작 날짜
 * @param endDate 종료 날짜
 * @returns "nn일간" 형태의 문자열
 */
export function getDurationText(startDate: Date | string, endDate: Date | string): string {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate

  // 날짜가 유효하지 않은 경우
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return ''
  }

  // 시작일과 종료일이 같은 경우
  if (start.getTime() === end.getTime()) {
    return '1일간'
  }

  // 밀리초 차이를 일수로 변환 (하루 = 24시간 * 60분 * 60초 * 1000밀리초)
  const timeDiff = Math.abs(end.getTime() - start.getTime())
  const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24)) + 1 // +1은 시작일 포함

  return `${daysDiff}일간`
}

/**
 * Clean text by removing problematic characters that can cause hydration errors
 */
export function cleanText(text: string | null | undefined): string {
  if (!text) return ''

  return (
    text
      // Remove replacement character () and other problematic unicode characters
      .replace(/\uFFFD/g, '') // Unicode replacement character
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '') // Control characters except \t, \n, \r
      .replace(/[\uE000-\uF8FF]/g, '') // Private Use Area
      .replace(/[\uFDD0-\uFDEF]/g, '') // Non-characters
      .replace(/[\uFFFE\uFFFF]/g, '') // Non-characters
      // Normalize whitespace
      .replace(/\s+/g, ' ')
      .trim()
  )
}

/**
 * Safe text wrapper for components that might receive problematic text
 */
export function safeText(text: string | null | undefined): string {
  try {
    return cleanText(text)
  } catch (error) {
    console.warn('Text cleaning failed:', error)
    return ''
  }
}

/**
 * 스크리닝 이벤트를 날짜별로 나눠주는 함수
 */
export const toDate = (value: string | number | Date) => new Date(value)

export const fmt = (value: string | number | Date, pattern: string) =>
  format(toDate(value), pattern)

/**
 * Asia/Seoul 고정 타임존으로 날짜를 포맷합니다. (DST 없음, UTC+9 고정)
 * 지원 패턴: 'HH:mm', 'M월 d일'
 */
export function fmtKST(
  value: string | number | Date,
  pattern: string,
  options?: { locale?: Locale }
): string {
  const date = toDate(value)
  if (isNaN(date.getTime())) return ''
  try {
    return formatInTimeZone(date, 'Asia/Seoul', pattern, options)
  } catch {
    return format(date, pattern, options as any)
  }
}

type GroupedDate = {
  key: string
  label: string
  iso: string
  screenings: any[]
}

export function groupScreeningsByDate(screenings: any[]): GroupedDate[] {
  if (!screenings || screenings.length === 0) return []
  const groupedByDateMap = screenings.reduce((acc: Map<string, GroupedDate>, curr: any) => {
    const durationDate = toDate(curr?.duration?.start)
    const key = fmt(durationDate, 'MM-dd')
    const label = fmt(durationDate, 'M월 d일')
    const iso = fmt(durationDate, 'yyyy-MM-dd')
    if (!acc.has(key)) acc.set(key, { key, label, iso, screenings: [] })
    acc.get(key)!.screenings.push(curr)
    //sort screenings by start time
    acc.get(key)!.screenings.sort((a: any, b: any) => {
      // 1) Hall (place) ascending as primary
      const aHallNum = Number.parseInt(String(a?.place ?? ''), 10)
      const bHallNum = Number.parseInt(String(b?.place ?? ''), 10)
      const aHasNum = !Number.isNaN(aHallNum)
      const bHasNum = !Number.isNaN(bHallNum)
      if (aHasNum && bHasNum && aHallNum !== bHallNum) return aHallNum - bHallNum
      if (aHasNum && !bHasNum) return -1
      if (!aHasNum && bHasNum) return 1

      // 2) Start time as tiebreaker
      const aStart = toDate(a?.duration?.start).getTime()
      const bStart = toDate(b?.duration?.start).getTime()
      return aStart - bStart
    })
    return acc
  }, new Map<string, GroupedDate>())

  return Array.from(groupedByDateMap.values()).sort((a, b) => a.iso.localeCompare(b.iso))
}

// program (films) util

/**
 * 동일 제목 + 끝자리 숫자만 다른 항목들을 하나로 묶어 요약 문자열을 생성합니다.
 * 예) ["고양이를 부탁해 1", "고양이를 부탁해 2"] → "고양이를 부탁해 1,2"
 * 가나다순(ko)으로 정렬합니다.
 */
export function summarizeTitlesByNumericSuffix(items: { title?: string }[]): string[] {
  if (!Array.isArray(items) || items.length === 0) return []
  type Group = { base: string; numbers: number[]; hasPlain: boolean }
  const map = new Map<string, Group>()

  for (const item of items) {
    const raw = String(item?.title ?? '').trim()
    if (!raw) continue
    const match = raw.match(/^(.*?)[\s]*([0-9]+)$/)
    if (match) {
      const base = match[1].trim()
      const num = Number.parseInt(match[2], 10)
      const group = map.get(base) || { base, numbers: [], hasPlain: false }
      if (!Number.isNaN(num) && !group.numbers.includes(num)) group.numbers.push(num)
      map.set(base, group)
    } else {
      const base = raw
      const group = map.get(base) || { base, numbers: [], hasPlain: false }
      group.hasPlain = true
      map.set(base, group)
    }
  }

  const groups = Array.from(map.values())
  groups.forEach(g => g.numbers.sort((a, b) => a - b))

  groups.sort((a, b) => a.base.localeCompare(b.base, 'ko'))

  return groups.map(g => {
    if (g.numbers.length > 0) return `${g.base} ${g.numbers.join(',')}`
    return g.base
  })
}

export function getHighestRating(programs: any[]): string | undefined {
  const ratingPriority: Record<string, number> = { g: 0, '12': 1, '15': 2, '19': 3 }
  const ratings = programs
    .map(p => p?.rating)
    .filter((v: any): v is string => typeof v === 'string')
  return ratings.reduce<string | undefined>((acc, cur) => {
    if (!acc) return cur
    const a = ratingPriority[acc] ?? -1
    const b = ratingPriority[cur] ?? -1
    return b > a ? cur : acc
  }, undefined)
}

export function getMinimalSubtitle(programs: any[]): string[] {
  const subtitlePriority: Record<string, number> = { n: 0, k: 1, e: 1, kn: 2, ke: 3 }
  const flatten = (v: any): string[] =>
    Array.isArray(v) ? v.filter((s: any): s is string => typeof s === 'string') : []
  const subtitles: string[] = programs.flatMap(p => flatten(p?.subtitles))
  const unique = Array.from(new Set(subtitles))
  // Sort by defined priority for stable display order
  unique.sort((a, b) => (subtitlePriority[a] ?? 999) - (subtitlePriority[b] ?? 999))
  return unique
}
